import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { fromWithCompany, supabase } from "../config/supabaseClient";
import { trainingFormStatus } from "../constant/constants";

/** Overview-of-training RPC: DB overload is (p_company_id, p_department_id, p_year_id) — always pass year. */
const RPC_EMPLOYEES_FORMS_DETAILS = "employees_forms_details";

/** RPC params for main (live) vs optimized (plan_optimizer_rows) dashboard source. */
export const dashboardSourceRpcParams = (sourceModule, planOptimizerId) => ({
  p_source_module: sourceModule === "optimized" ? "optimized" : "main",
  p_source_reference_id:
    sourceModule === "optimized" && planOptimizerId ? planOptimizerId : null,
});

export const materializeDashboardPlanSnapshot = createAsyncThunk(
  "dashboard/materializePlanSnapshot",
  async (planOptimizerId, { rejectWithValue }) => {
    try {
      const { data, error } = await supabase.rpc(
        "materialize_dashboard_plan_snapshot",
        { p_plan_optimizer_id: planOptimizerId },
      );
      if (error) return rejectWithValue(error.message);
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const fetchEmployeeDemographics = createAsyncThunk(
  "dashboard/fetchEmployeeDemographics",
  async ({ company_id, department_id, training_need_workflow }, { rejectWithValue }) => {
    try {
      const { data, error } = await supabase.rpc(training_need_workflow ? "structured_employee_demographics" : "employee_demographics", {
        p_company_id: company_id,
        p_department_id: department_id,
      });
      if (error) {
        return rejectWithValue(error.message || String(error));
      }
      return data;
    } catch (error) {
      return rejectWithValue(
        error?.message || error?.response?.data || String(error),
      );
    }
  }
);

export const fetchTrainingPlanDetails = createAsyncThunk(
  "dashboard/fetchTrainingPlanDetails",
  async (
    { company_id, department_id, yearId, sourceModule, planOptimizerId },
    { rejectWithValue },
  ) => {
    if (!company_id || !yearId) {
      return rejectWithValue(
        `${RPC_EMPLOYEES_FORMS_DETAILS} requires company_id and yearId`,
      );
    }
    try {
      const { data, error } = await supabase.rpc(RPC_EMPLOYEES_FORMS_DETAILS, {
        p_company_id: company_id,
        p_department_id: department_id,
        p_year_id: yearId,
        ...dashboardSourceRpcParams(sourceModule, planOptimizerId),
      });
      if (error) {
        return rejectWithValue(error.message || String(error));
      }
      return data;
    } catch (error) {
      return rejectWithValue(
        error?.message || error?.response?.data || String(error),
      );
    }
  }
);

export const fetchDepartmentBudgetDetails = createAsyncThunk(
  "dashboard/fetchDepartmentBudgetDetails",
  async (
    {
      company_id,
      department_id,
      isTrainingEnabled,
      yearId,
      sourceModule,
      planOptimizerId,
    },
    { rejectWithValue },
  ) => {
    try {
      const { data, error } = await supabase.rpc(
        isTrainingEnabled ? "structured_deparment_wise_bifurcation" : "department_wise_bifurcation",
        {
          p_company_id: company_id,
          p_year_id: yearId,
          p_department_id: department_id,
          ...dashboardSourceRpcParams(sourceModule, planOptimizerId),
        }
      );
      if (error) {
        return rejectWithValue(error.message || String(error));
      }
      return data;
    } catch (error) {
      return rejectWithValue(
        error?.message || error?.response?.data || String(error),
      );
    }
  }
);

export const fetchDepartmentBudgetDetailsDisabled = createAsyncThunk(
  "dashboard/fetchDepartmentBudgetDetailsDisabled",
  async (
    { company_id, department_id, yearId, sourceModule, planOptimizerId },
    { rejectWithValue },
  ) => {
    try {
      const { data, error } = await supabase.rpc(
        "structured_deparment_wise_bifurcation_for_disabled",
        {
          p_company_id: company_id,
          p_year_id: yearId,
          p_department_id: department_id,
          ...dashboardSourceRpcParams(sourceModule, planOptimizerId),
        },
      );
      if (error) {
        return rejectWithValue(error.message || String(error));
      }
      return data;
    } catch (error) {
      return rejectWithValue(
        error?.message || error?.response?.data || String(error),
      );
    }
  },
);

export const BBEE_bifurcation = createAsyncThunk(
  "dashboard/BBEE_bifurcation",
  async (
    {
      company_id,
      yearId,
      department_id,
      trainind_need_flow,
      sourceModule,
      planOptimizerId,
    },
    { rejectWithValue },
  ) => {
    try {
      // structured_bbee_bifurcation
      const { data, error } = await supabase.rpc(trainind_need_flow ? "structured_bbee_bifurcation" : "bbee_bifurcation", {
        p_company_id: company_id,
        p_year_id: yearId,
        p_department_id: department_id,
        ...dashboardSourceRpcParams(sourceModule, planOptimizerId),
      });
      if (error) {
        return rejectWithValue(error.message || String(error));
      }
      return data;
    } catch (error) {
      return rejectWithValue(
        error?.message || error?.response?.data || String(error),
      );
    }
  }
);

export const topTrainingsList = createAsyncThunk(
  "dashboard/topTrainingsList",
  async (
    {
      company_id,
      yearId,
      department_id,
      trainind_need_flow,
      sourceModule,
      planOptimizerId,
    },
    { rejectWithValue },
  ) => {
    try {
      // structured_top_trainings_list
      const { data, error } = await supabase.rpc(trainind_need_flow ? "structured_top_trainings_list" : "top_training_list", {
        p_company_id: company_id,
        p_year_id: yearId,
        p_department_id: department_id,
        ...dashboardSourceRpcParams(sourceModule, planOptimizerId),
      });
      if (error) {
        return rejectWithValue(error.message || String(error));
      }
      return data;
    } catch (error) {
      return rejectWithValue(
        error?.message || error?.response?.data || String(error),
      );
    }
  }
);

export const trainingCategoryBudgetConfig = createAsyncThunk(
  "dashboard/trainingCategoryBudgetConfig",
  async (
    {
      company_id,
      yearId,
      department_id,
      training_need_workflow,
      sourceModule,
      planOptimizerId,
    },
    { rejectWithValue },
  ) => {
    try {
      const { data, error } = await supabase.rpc(training_need_workflow ? 'structured_training_type_data' : 'training_type_data', {
        p_company_id: company_id,
        p_year_id: yearId,  
        p_department_id: department_id,
        ...dashboardSourceRpcParams(sourceModule, planOptimizerId),
      });
      if (error) {
        return rejectWithValue(error.message || String(error));
      }
      return data;
    } catch (error) {
      return rejectWithValue(
        error?.message || error?.response?.data || String(error),
      );
    } 
  }
);

export const fetchAdhocTrainingDetails = createAsyncThunk(  
  "dashboard/fetchAdhocTrainingDetails",
  async (_, { rejectWithValue }) => {
    try {
      const { data, error } = await fromWithCompany('training_requests').select('*');
      if (error) {
        return rejectWithValue(error.message || String(error));
      }
      return data;
    } catch (error) {
      return rejectWithValue(
        error?.message || error?.response?.data || String(error),
      );
    }
  }
);

export const fetchCriticalSkillsSummary = createAsyncThunk(
  "dashboard/fetchCriticalSkillsSummary",
  async ({ company_id, yearId }, { rejectWithValue }) => {
    try {
      // Get all critical trainings from company catalogue
      const { data: companyCriticalTrainings, error: companyError } = await supabase
        .from("training_catalogue_data")
        .select("id, name, cost, provider, company_id")
        .eq("company_id", company_id)
        .eq("is_critical", true)
        .eq("status", "active");

      if (companyError) throw companyError;

      // Get all critical trainings from iFundi catalogue (where company_id is null)
      const { data: ifundiCriticalTrainings, error: ifundiError } = await supabase
        .from("training_catalogue_data_types")
        .select("training_id, is_critical, training_catalogue_data!inner(id, name, cost, provider, status)")
        .eq("company_id", company_id)
        .eq("is_critical", true)
        .eq("training_catalogue_data.status", "active");

      if (ifundiError) throw ifundiError;

      // Combine both types of critical trainings
      const allCriticalTrainings = [];
      
      // Add company catalogue trainings
      if (companyCriticalTrainings) {
        companyCriticalTrainings.forEach(training => {
          allCriticalTrainings.push({
            training_id: training.id,
            training_name: training.name,
            cost: training.cost || 0,
            provider: training.provider || "N/A",
            company_id: training.company_id,
          });
        });
      }

      // Add iFundi catalogue trainings
      if (ifundiCriticalTrainings) {
        ifundiCriticalTrainings.forEach(item => {
          if (item.training_catalogue_data) {
            allCriticalTrainings.push({
              training_id: item.training_id,
              training_name: item.training_catalogue_data.name,
              cost: item.training_catalogue_data.cost || 0,
              provider: item.training_catalogue_data.provider || "N/A",
              company_id: null,
            });
          }
        });
      }

      // Get training IDs for querying allocations
      const trainingIds = allCriticalTrainings.map(t => t.training_id);

      if (trainingIds.length === 0) {
        return [];
      }

      // Get year_id if not provided
      let finalYearId = yearId;
      if (!finalYearId) {
        const { data: yearData } = await supabase
          .from("year")
          .select("id")
          .eq("company_id", company_id)
          .order("start_date", { ascending: false })
          .limit(1)
          .maybeSingle();
        finalYearId = yearData?.id;
      }

      if (!finalYearId) {
        return allCriticalTrainings.map(t => ({
          ...t,
          budget: 0,
          learner_count: 0,
        }));
      }

      // First, get all forms with their latest status
      // We need to filter for forms that are submitted and recommended (Approved status)
      const { data: allForms, error: formsError } = await supabase
        .from("training_survey_form")
        .select(`
          id,
          emp_id,
          year_id,
          form_status(id, status, created_at)
        `)
        .eq("company_id", company_id)
        .eq("year_id", finalYearId)
        .eq("status", "active");

      if (formsError) {
        console.error("Error fetching forms:", formsError);
        return allCriticalTrainings.map(t => ({
          ...t,
          budget: 0,
          learner_count: 0,
        }));
      }

      // Include forms whose latest status is Recommended or Approved.
      const approvedFormIds = [];
      if (allForms) {
        allForms.forEach(form => {
          if (form.form_status && form.form_status.length > 0) {
            const nonDraftStatuses = form.form_status
              .filter(fs => fs.status && fs.status !== trainingFormStatus.DRAFT)
              .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

            const latestStatus = nonDraftStatuses[0];

            if (
              latestStatus &&
              (latestStatus.status === trainingFormStatus.RECOMMENDED ||
                latestStatus.status === trainingFormStatus.APPROVED)
            ) {
              approvedFormIds.push(form.id);
            }
          }
        });
      }

      if (approvedFormIds.length === 0) {
        return allCriticalTrainings.map(t => ({
          ...t,
          budget: 0,
          learner_count: 0,
        }));
      }

      const { data: allocatedData, error: allocatedError } = await supabase
        .from("training_survey_form")
        .select(`
          id,
          emp_id,
          year_id,
          skill_requirements(
            selected_trainings(
              training_id,
              training_catalogue_data(id, cost)
            )
          )
        `)
        .eq("company_id", company_id)
        .eq("year_id", finalYearId)
        .in("id", approvedFormIds);

      if (allocatedError) {
        console.error("Error fetching allocated data:", allocatedError);
        // Return trainings with zero values if allocation query fails
        return allCriticalTrainings.map(t => ({
          ...t,
          budget: 0,
          learner_count: 0,
        }));
      }

      // Calculate budget and learner count for each critical training
      const summaryData = allCriticalTrainings.map(training => {
        let totalBudget = 0;
        const uniqueLearners = new Set();

        if (allocatedData) {
          allocatedData.forEach(form => {
            if (form.skill_requirements) {
              form.skill_requirements.forEach(skill => {
                if (skill.selected_trainings) {
                  skill.selected_trainings.forEach(selectedTraining => {
                    if (selectedTraining.training_id === training.training_id) {
                      // Add learner
                      uniqueLearners.add(form.emp_id);
                      
                      // Add budget (use cost from training or allocated cost)
                      const trainingCost = selectedTraining.training_catalogue_data?.cost || training.cost || 0;
                      totalBudget += Number(trainingCost) || 0;
                    }
                  });
                }
              });
            }
          });
        }

        return {
          training_id: training.training_id,
          training_name: training.training_name,
          budget: totalBudget,
          learner_count: uniqueLearners.size,
          cost: training.cost,
          provider: training.provider,
        };
      });

      // Filter out trainings where both budget and learner_count are 0
      const filteredSummaryData = summaryData.filter(
        (training) => !(training.budget === 0 && training.learner_count === 0)
      );

      return filteredSummaryData;
    } catch (error) {
      console.error("Error fetching critical skills summary:", error);
      return rejectWithValue(error.message || "Failed to fetch critical skills summary");
    }
  }
);

export const dashboardSlice = createSlice({
  name: "dashboard",
  initialState: {
    employeeDemographics: {
      total_employees: 0,
      total_departments: 0,
    },
    error: null,
    /**
     * Cache per logged-in user so data is not visible to other users.
     * Key: userId (e.g. emp_id). Value: { dashboardCacheTimestamp, cachedChartData }.
     * cachedChartData.budgetBifurcationByDepartment: { [departmentId]: { budgetWiseBifurcationData } } for per-department cache.
     * cachedChartData.organizationalTrainingSnapshot: { employmentSnapshot, unemployedInvestment } for snapshot cards after remount.
     */
    cacheByUser: {},
  },
  reducers: {
    setDashboardCacheTimestamp: (state, action) => {
      const { userId, timestamp } = action.payload;
      if (!state.cacheByUser[userId]) state.cacheByUser[userId] = { dashboardCacheTimestamp: null, cachedChartData: null };
      state.cacheByUser[userId].dashboardCacheTimestamp = timestamp;
    },
    clearDashboardCache: (state, action) => {
      const userId = action.payload;
      if (state.cacheByUser[userId]) state.cacheByUser[userId] = { dashboardCacheTimestamp: null, cachedChartData: null };
    },
    /** Merge chart data into cache for the given user. budgetBifurcationByDepartment is merged by department id. */
    setDashboardChartCache: (state, action) => {
      const { userId, payload } = action.payload;
      if (!state.cacheByUser[userId]) state.cacheByUser[userId] = { dashboardCacheTimestamp: null, cachedChartData: null };
      if (!state.cacheByUser[userId].cachedChartData) state.cacheByUser[userId].cachedChartData = {};
      const dest = state.cacheByUser[userId].cachedChartData;
      if (payload.budgetBifurcationByDepartment && typeof payload.budgetBifurcationByDepartment === "object") {
        if (!dest.budgetBifurcationByDepartment) dest.budgetBifurcationByDepartment = {};
        Object.assign(dest.budgetBifurcationByDepartment, payload.budgetBifurcationByDepartment);
      }
      const rest = { ...payload };
      delete rest.budgetBifurcationByDepartment;
      Object.assign(dest, rest);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchEmployeeDemographics.fulfilled, (state, action) => {
      state.employeeDemographics = action.payload;
    });
    builder.addCase(fetchEmployeeDemographics.rejected, (state, action) => {
      state.error = action.payload;
    });
  },
});

export const { startLoading, stopLoading, setDashboardCacheTimestamp, clearDashboardCache, setDashboardChartCache } = dashboardSlice.actions;
const { reducer } = dashboardSlice;
export default reducer;
